import React, { ReactNode, useEffect, useState } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classNames from 'classnames';

import { setTitle } from '~main/actions/login';

export interface Tab {
  id: string;
  title: string;
  component?: React.FC<any>;
  content?: ReactNode;
  authTag?: string;
}

interface ComponentProps {
  tabs: Tab[];
  className?: string;
  setPageTitle?: boolean;
  activeTab?: number;
  onTabChange?: (index: number) => void;
}

// Derived from the caller's own tab id so `aria-controls`/`aria-labelledby` can point at each other
// without the caller having to supply a second id.
const tabId = (id: string) => `${id}-tab-control`;
const panelId = (id: string) => `${id}-tab-panel`;

const TabContainer: React.FC<ComponentProps> = ({
  tabs,
  className,
  setPageTitle = true,
  activeTab: controlledActiveTab,
  onTabChange,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<number>(0);

  const isControlled = controlledActiveTab !== undefined;
  const requestedActiveTab = isControlled ? controlledActiveTab : internalActiveTab;
  // Clamped at BOTH ends, never used raw. Out-of-range either way selects no TabPane at all, so the
  // body renders blank with no way back except closing whatever contains it.
  //
  // Above: `tabs` is a prop and callers rebuild it from live data, so the set can SHRINK while an
  // index into the old set is still held - the first such caller is ClaimItemRequirements, whose
  // Attachments tab disappears the moment the item's last attachment is deleted.
  //
  // Below: a controlled caller computing its index from `indexOf`/`findIndex` gets -1 on a miss.
  // FormsContainer already guards that with its own `Math.max(0, ...)`, which is the point - the
  // invariant belongs here, once, rather than in every caller that has to remember it.
  //
  // The inner `Math.max(..., 0)` is separate again: it keeps an EMPTY `tabs` array at 0 rather than
  // clamping the upper bound to -1.
  const activeTab = Math.max(0, Math.min(requestedActiveTab, Math.max(tabs.length - 1, 0)));

  const handleTabChange = (index: number) => {
    if (isControlled) {
      onTabChange?.(index);
    } else {
      setInternalActiveTab(index);
    }
  };

  // Left/Right move between tabs, Home/End jump to the ends - the WAI-ARIA tabs pattern. Wrapping at
  // both ends (rather than stopping) matches the pattern's "optional but recommended" behaviour and
  // means a two-tab strip toggles with a single key. Focus follows selection, which is the correct
  // choice here: every panel's content is already mounted, so there is no cost to activating on move.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
    const lastIndex = tabs.length - 1;
    let nextIndex: number | null = null;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = lastIndex;

    if (nextIndex === null) return;

    // Arrow keys scroll the page by default, and Home/End jump it to the ends.
    event.preventDefault();
    handleTabChange(nextIndex);
    document.getElementById(tabId(tabs[nextIndex].id))?.focus();
  };

  const navClassNames = (index: number) => classNames({ active: activeTab === index });

  const buildNavTabs = () => {
    return tabs.map(({ id, title }, index) => (
      // role="presentation" on the <li>: the ARIA tabs pattern requires `tab` to be a child of
      // `tablist`, and reactstrap's NavItem puts an <li> in between. Marking it presentational is
      // what keeps the required-children rule satisfied (the same markup Bootstrap 5's own tabs use).
      <NavItem key={index} role="presentation">
        {/*
          tag="button": NavLink defaults to an <a>, and an <a> with no href is not focusable, has no
          implicit role and takes no keyboard activation - which left this strip reachable by mouse
          only. Bootstrap 5.3's `.nav-link` sets `background: none; border: 0`, so a <button> renders
          identically to the anchor it replaces.
        */}
        <NavLink
          tag="button"
          type="button"
          id={tabId(id)}
          role="tab"
          aria-selected={activeTab === index}
          aria-controls={panelId(id)}
          data-cy={`${id}-tab`}
          className={navClassNames(index)}
          onClick={() => handleTabChange(index)}
          onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => handleKeyDown(event, index)}
        >
          {title}
        </NavLink>
      </NavItem>
    ));
  };

  const buildTabsContent = () => {
    return tabs.map(({ id, component, content, title, authTag }, index) => {
      const Component = component;

      return (
        <TabPane key={index} tabId={index} id={panelId(id)} role="tabpanel" aria-labelledby={tabId(id)}>
          {Component ? <Component isActive={activeTab === index} title={title} authTag={authTag} /> : content}
        </TabPane>
      );
    });
  };

  const outerClassName = classNames('p-2 tab-container', className);

  useEffect(() => {
    // Optional chaining, not `tabs[activeTab].title`: the clamp above keeps the index in range for a
    // non-empty array, but an empty `tabs` still has nothing at index 0.
    const activeTitle = tabs[activeTab]?.title;
    if (setPageTitle && activeTitle) setTitle(activeTitle);
  }, [activeTab, setPageTitle]);

  return (
    <div className={outerClassName}>
      <Nav tabs role="tablist">
        {buildNavTabs()}
      </Nav>
      <TabContent className="p-2" activeTab={activeTab}>
        {buildTabsContent()}
      </TabContent>
    </div>
  );
};

export default TabContainer;
